import { call } from '@advanza/api'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FilterValue } from 'components/reports/common/ReportFilter'

// Types
export interface Filters {
    subscription_status?: string
    start_date?: string
    end_date?: string
    service_id?: string | string[] | number[]
    provider_status?: string | number
    churn_reason?: string | number
    win_back_shown_start_date?: string
    win_back_shown_end_date?: string
    [key: string]: FilterValue
}

export interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

export interface WinBackOffersReportData {
    provider_id: number
    provider_status: number
    subscription_status: string
    subscription_start_date: string | null
    branch: string | null
    first_invoice_subscription_month: string | null
    prospect_value: number
    churn_reason: string | null
    win_back_offer_seen: boolean
    win_back_offer_variant: string | null
    win_back_offer_deal: string | null
    win_back_offer_accepted: boolean
    win_back_offer_date: string | null
    win_back_shown_date: string | null
    churn_comment: string | null
}

export interface Sorting {
    field: string | null
    direction: 'ASC' | 'DESC' | null
}

export interface WinBackReasonBreakdown {
    reason_id: number | null
    reason_label: string
    shown: number
    accepted: number
    acceptance_rate: number
}

export interface WinBackDealBreakdown {
    deal: string
    shown: number
    accepted: number
    acceptance_rate: number
}

export interface WinBackOffersAnalytics {
    offers_shown: number
    offers_accepted: number
    acceptance_rate: number
    offers_declined: number
    cs_bookings_dismissed: number
    repeat_acceptances: number
    accepters_still_active: number
    by_reason: WinBackReasonBreakdown[]
    by_deal: WinBackDealBreakdown[]
}

export interface WinBackOffersReportState {
    data: WinBackOffersReportData[]
    pages: Record<number, string[]>
    rows: Record<string, any>
    pagination: Pagination | null
    filters: Filters
    sorting: Sorting
    isLoading: boolean
    error: string | null
    analytics: WinBackOffersAnalytics | null
    analyticsLoading: boolean
}

// API Response Types
interface ApiResponse {
    items: WinBackOffersReportData[]
    count: number
}

// Async Thunks
export const fetchWinBackOffersReport = createAsyncThunk(
    'winBackOffersReport/fetchData',
    async (
        params: {
            filters: Filters
            page?: number
            limit?: number
            sorting?: Sorting
            country?: string
        },
        { rejectWithValue },
    ) => {
        try {
            const { filters, page = 0, limit = 50, sorting, country } = params

            const queryParams = {
                ...filters,
                page,
                limit,
                ...(sorting?.field && { sort_field: sorting.field }),
                ...(sorting?.direction && { sort_direction: sorting.direction }),
                ...(country && { country }),
            }

            const response: ApiResponse = await call('office/win-back-offers-report', {
                method: 'GET',
                query: queryParams,
            })

            // Create pages and rows structure for TableList
            const pages: Record<number, string[]> = {}
            const rows: Record<string, WinBackOffersReportData> = {}

            // The backend already returns paginated results, so we just need to create the current page
            const currentPage = page
            const pageItems = response.items

            // Create the current page
            pages[currentPage] = pageItems.map((item, index) => `${item.provider_id}_${index}`)

            // Create rows for current page
            pageItems.forEach((item, index) => {
                const id = `${item.provider_id}_${index}`
                rows[id] = item
            })

            const totalPages = Math.ceil(response.count / limit)

            const pagination: Pagination = {
                page,
                limit,
                total: response.count,
                pages: totalPages,
            }

            return {
                data: response.items,
                pages,
                rows,
                pagination,
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to fetch win-back offers report'
            return rejectWithValue(errorMessage)
        }
    },
)

// Default the report to the current month so the initial (and cleared) load is
// bounded to a small, fast window. Users can widen the range via the filter.
export const firstOfCurrentMonth = (): string => {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    return `${now.getFullYear()}-${month}-01`
}

export const fetchWinBackOffersAnalytics = createAsyncThunk(
    'winBackOffersReport/fetchAnalytics',
    async (params: { filters: Filters; country?: string }, { rejectWithValue }) => {
        try {
            const { filters, country } = params

            const response: { analytics: WinBackOffersAnalytics } = await call(
                'office/win-back-offers-report/analytics',
                {
                    method: 'GET',
                    query: {
                        ...filters,
                        ...(country && { country }),
                    },
                },
            )

            return response.analytics
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to fetch win-back analytics'
            return rejectWithValue(errorMessage)
        }
    },
)

const initialState: WinBackOffersReportState = {
    data: [],
    pages: {},
    rows: {},
    pagination: null,
    filters: { win_back_shown_start_date: firstOfCurrentMonth() },
    sorting: {
        field: null,
        direction: null,
    },
    isLoading: false,
    error: null,
    analytics: null,
    analyticsLoading: false,
}

const winBackOffersReportSlice = createSlice({
    name: 'winBackOffersReport',
    initialState,
    reducers: {
        requestStarted: (state) => {
            state.isLoading = true
            state.error = null
        },
        requestSuccess: (
            state,
            action: PayloadAction<{
                data: WinBackOffersReportData[]
                pages: Record<number, string[]>
                rows: Record<string, any>
                pagination: Pagination | null
            }>,
        ) => {
            state.isLoading = false
            state.data = action.payload.data
            state.pages = action.payload.pages
            state.rows = action.payload.rows
            state.pagination = action.payload.pagination
            state.error = null
        },
        requestFailed: (state, action: PayloadAction<string>) => {
            state.isLoading = false
            state.error = action.payload
        },
        filtersChanged: (state, action: PayloadAction<Filters>) => {
            state.filters = action.payload
        },
        pageChanged: (state, action: PayloadAction<number>) => {
            if (state.pagination) {
                state.pagination.page = action.payload
            }
        },
        changeFilter: (state, action: PayloadAction<{ key: string; value: FilterValue }>) => {
            state.filters[action.payload.key as keyof Filters] = action.payload
                .value as Filters[keyof Filters]
        },
        clearFilters: (state) => {
            state.filters = {}
        },
        setSorting: (
            state,
            action: PayloadAction<{ field: string; direction: 'ASC' | 'DESC' }>,
        ) => {
            state.sorting = {
                field: action.payload.field,
                direction: action.payload.direction,
            }
        },
        clearSorting: (state) => {
            state.sorting = {
                field: null,
                direction: null,
            }
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWinBackOffersReport.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchWinBackOffersReport.fulfilled, (state, action) => {
                state.isLoading = false
                state.data = action.payload.data
                state.pages = action.payload.pages
                state.rows = action.payload.rows
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchWinBackOffersReport.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
            .addCase(fetchWinBackOffersAnalytics.pending, (state) => {
                state.analyticsLoading = true
            })
            .addCase(fetchWinBackOffersAnalytics.fulfilled, (state, action) => {
                state.analyticsLoading = false
                state.analytics = action.payload
            })
            .addCase(fetchWinBackOffersAnalytics.rejected, (state) => {
                state.analyticsLoading = false
            })
    },
})

export const {
    requestStarted,
    requestSuccess,
    requestFailed,
    filtersChanged,
    pageChanged,
    changeFilter,
    clearFilters,
    setSorting,
    clearSorting,
    resetState,
} = winBackOffersReportSlice.actions

export default winBackOffersReportSlice.reducer
